import {
  CheckCircle2,
  FileText,
  FolderLock,
  LayoutDashboard,
  LockKeyhole,
  Mail,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import type { ReactNode } from "react";

interface BrowserFrameProps {
  address: string;
  children: ReactNode;
  label: string;
}

function BrowserFrame({ address, children, label }: BrowserFrameProps) {
  return (
    <div
      role="img"
      aria-label={label}
      className="border-border overflow-hidden rounded-2xl border bg-white shadow-2xl shadow-black/15"
    >
      <div className="flex items-center gap-3 border-b border-zinc-200 bg-zinc-100 px-3 py-2.5 sm:px-4">
        <div className="flex shrink-0 gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff745a]" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </div>
        <div className="min-w-0 flex-1 rounded-md border border-zinc-200 bg-white px-3 py-1 text-center text-[10px] text-zinc-500 sm:text-xs">
          <span className="block truncate">{address}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

const documents = [
  { name: "Will & Testament.pdf", detail: "Updated 12 Jun 2026" },
  { name: "Insurance Portfolio.pdf", detail: "Updated 4 May 2026" },
  { name: "Letter to Family.pdf", detail: "Updated 18 Apr 2026" },
];

export function VaultDashboardMockup() {
  return (
    <BrowserFrame
      address="vault.apexfinancial.com.au/john-smith"
      label="Mockup of John Smith's secure vault managed by Apex Financial"
    >
      <div className="bg-[#f7f5f2]">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#5d405c] text-sm font-bold text-white">
              A
            </div>
            <div>
              <p className="text-xs font-bold text-[#332532] sm:text-sm">
                Apex Financial
              </p>
              <p className="text-[9px] text-zinc-500 sm:text-[10px]">
                Private Client Vault
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-right sm:block">
              <span className="block text-[10px] font-semibold text-zinc-700">
                John Smith
              </span>
              <span className="block text-[9px] text-zinc-400">
                Vault owner
              </span>
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#ffe1da] text-[10px] font-bold text-[#cc5c48]">
              JS
            </span>
          </div>
        </div>

        <div className="flex min-h-[390px] sm:min-h-[430px]">
          <aside className="hidden w-44 shrink-0 border-r border-zinc-200 bg-white p-4 md:block">
            <p className="mb-3 px-2 text-[9px] font-semibold tracking-wider text-zinc-400 uppercase">
              John&apos;s vault
            </p>
            {[
              { icon: LayoutDashboard, label: "Overview", active: true },
              { icon: FolderLock, label: "Documents" },
              { icon: UsersRound, label: "Trustees" },
              { icon: ShieldCheck, label: "Security" },
            ].map((item) => (
              <div
                key={item.label}
                className={`mb-1 flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] ${
                  item.active
                    ? "bg-[#fff0ed] font-semibold text-[#cc5c48]"
                    : "text-zinc-500"
                }`}
              >
                <item.icon className="h-3.5 w-3.5" />
                {item.label}
              </div>
            ))}
            <div className="mt-20 rounded-xl bg-[#5d405c] p-3 text-white">
              <LockKeyhole className="mb-2 h-4 w-4 text-[#ff745a]" />
              <p className="text-[10px] font-semibold">Encrypted in-browser</p>
              <p className="mt-1 text-[9px] leading-relaxed text-zinc-300">
                File contents remain private.
              </p>
            </div>
          </aside>

          <main className="min-w-0 flex-1 p-4 text-left sm:p-6">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
              <div>
                <div className="mb-1 flex items-center gap-2">
                  <h3 className="font-display text-lg font-bold text-[#332532] sm:text-2xl">
                    John Smith&apos;s Vault
                  </h3>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[9px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Protected
                  </span>
                </div>
                <p className="text-[10px] text-zinc-500 sm:text-xs">
                  Managed by Apex Financial
                </p>
              </div>
              <div className="rounded-lg border border-[#eadfe8] bg-white px-3 py-2">
                <p className="text-[9px] text-zinc-400">Vault readiness</p>
                <p className="mt-0.5 text-sm font-bold text-[#5d405c]">75%</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { value: "8", label: "Documents" },
                { value: "3", label: "Trustees" },
                { value: "2 of 3", label: "Release threshold" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border border-zinc-200 bg-white p-3 sm:p-4"
                >
                  <p className="text-base font-bold text-[#5d405c] sm:text-xl">
                    {stat.value}
                  </p>
                  <p className="mt-0.5 text-[8px] leading-tight text-zinc-400 sm:text-[10px]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[1.25fr_0.75fr]">
              <div className="rounded-xl border border-zinc-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-xs font-bold text-[#332532]">
                    Recent documents
                  </p>
                  <span className="text-[9px] font-semibold text-[#cc5c48]">
                    View all
                  </span>
                </div>
                <div className="space-y-2">
                  {documents.map((document) => (
                    <div
                      key={document.name}
                      className="flex items-center gap-2.5 rounded-lg bg-zinc-50 p-2.5"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#fff0ed]">
                        <FileText className="h-3.5 w-3.5 text-[#cc5c48]" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[10px] font-semibold text-zinc-700">
                          {document.name}
                        </span>
                        <span className="block text-[8px] text-zinc-400">
                          {document.detail}
                        </span>
                      </span>
                      <LockKeyhole className="ml-auto h-3 w-3 shrink-0 text-zinc-300" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="hidden rounded-xl border border-zinc-200 bg-white p-4 sm:block">
                <p className="text-xs font-bold text-[#332532]">
                  Nominated trustees
                </p>
                <div className="mt-4 flex -space-x-2">
                  {["OS", "MS", "SL"].map((initials, index) => (
                    <span
                      key={initials}
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white text-[9px] font-bold"
                      style={{
                        backgroundColor: ["#ffe1da", "#eadfe8", "#e4eee9"][
                          index
                        ],
                        color: ["#cc5c48", "#5d405c", "#34705a"][index],
                      }}
                    >
                      {initials}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-[9px] leading-relaxed text-zinc-500">
                  Olivia, Michael and Sarah have been nominated. No single
                  trustee can access the vault alone.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </BrowserFrame>
  );
}

export function BrandedLoginMockup() {
  return (
    <BrowserFrame
      address="clients.apexfinancial.com.au/sign-in"
      label="Mockup of a fully branded Apex Financial client vault sign-in page"
    >
      <div className="flex min-h-[520px] flex-col bg-[#f8f5f1]">
        <div className="flex items-center justify-between border-b border-[#e8e0d8] bg-white/80 px-5 py-4 backdrop-blur">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#5d405c] text-sm font-bold text-white">
              A
            </div>
            <div>
              <p className="font-display text-sm font-bold text-[#332532]">
                Apex Financial
              </p>
              <p className="text-[9px] tracking-wide text-zinc-400 uppercase">
                Private Client Services
              </p>
            </div>
          </div>
          <span className="text-[10px] font-medium text-zinc-500">
            Need help?
          </span>
        </div>

        <div className="flex flex-1 items-center justify-center p-5 sm:p-8">
          <div className="w-full max-w-sm rounded-2xl border border-[#e8e0d8] bg-white p-6 text-left shadow-xl shadow-[#5d405c]/10 sm:p-8">
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#fff0ed]">
              <LockKeyhole className="h-5 w-5 text-[#cc5c48]" />
            </div>
            <p className="text-[10px] font-semibold tracking-wider text-[#cc5c48] uppercase">
              Secure client portal
            </p>
            <h3 className="font-display mt-2 text-2xl font-bold text-[#332532]">
              Welcome to your vault
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-zinc-500">
              Sign in to access your Apex Financial private client vault.
            </p>

            <div className="mt-6">
              <p className="mb-1.5 text-[10px] font-semibold text-zinc-600">
                Email address
              </p>
              <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-[11px] text-zinc-500">
                <Mail className="h-3.5 w-3.5 text-zinc-400" />
                john.smith@example.com
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-[#ff745a] px-4 py-3 text-center text-xs font-semibold text-white shadow-lg shadow-[#ff745a]/20">
              Send secure sign-in link
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-lg bg-[#f7f5f2] p-3">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#5d405c]" />
              <p className="text-[9px] leading-relaxed text-zinc-500">
                Your documents are encrypted on your device before upload.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 border-t border-[#e8e0d8] px-5 py-3 text-[9px] text-zinc-400">
          <span>&copy; 2026 Apex Financial</span>
          <span>Privacy</span>
          <span>Support</span>
        </div>
      </div>
    </BrowserFrame>
  );
}
